import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { auth, apiFetch, getHasSession, clearAuthState as clearApiAuthState } from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [emailVerified, setEmailVerified] = useState(true);
  const [showVerificationBanner, setShowVerificationBanner] = useState(false);
  const sessionStartRef = useRef(Date.now());
  const firstUploadTrackedRef = useRef(false);

  const handleLogout = useCallback(() => {
    clearApiAuthState();
    auth.logout();
    setUser(null);
    setEmailVerified(true);
    setShowVerificationBanner(false);
  }, []);

  const checkVerificationStatus = useCallback(async () => {
    try {
      const response = await apiFetch('/auth/verification-status');
      if (response.status === 401) {
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setEmailVerified(data.verified);
        setShowVerificationBanner(!data.verified);
      }
    } catch (err) {
      if (String(err?.message || '').includes('UNAUTHORIZED')) {
        return;
      }
      console.error('Failed to check verification:', err);
    }
  }, []);

  const value = {
    user,
    setUser,
    emailVerified,
    setEmailVerified,
    showVerificationBanner,
    setShowVerificationBanner,
    sessionStartRef,
    firstUploadTrackedRef,
    handleLogout,
    checkVerificationStatus,
    getHasSession,
    clearApiAuthState
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
